// ─── Subscription Management Types ──────────────────────────────────────────
// Frontend-friendly camelCase; hook maps to/from backend snake_case.

export interface Subscription {
  id: string;
  name: string;
  vendor: string;
  department: string;
  category: string;
  owner: string;
  approvedBy: string;
  billingCycle: string;
  amount: number;
  currency: string;
  monthlyCost: number;
  startDate: string;
  renewalDate: string;
  paymentMethod: string;
  approvedBudget: string; // Monthly, Quarterly, Annually, One Time
  status: string; // Active, Paused, Cancelled
  approvalStatus: string; // Approved, Pending-Approval
  notes: string;
}

export interface Invoice {
  id: string;
  subscriptionId: string;
  invoiceNumber: string;
  invoiceDate: string;
  /** Coverage period start (sheet: Subscription Start Date). */
  subscriptionStartDate?: string;
  /** Coverage period end — preferred renewal source when Paid (sheet: Subscription End Date). */
  subscriptionEndDate?: string;
  amount: number;
  currency: string;
  paymentMethod: string;
  paymentStatus: string; // server-side default: 'Unpaid'
  invoiceFile: string;
}

/** Display-only option derived from subscription rows (no master vendor table). */
export interface Vendor {
  id: string;
  name: string;
  category: string;
}

/** Display-only option derived from subscription rows. */
export interface PaymentMethod {
  id: string;
  name: string;
  type: string;
}

export interface SubscriptionSnapshot {
  subscriptions: Subscription[];
  invoices: Invoice[];
  vendors: Vendor[];
  paymentMethods: PaymentMethod[];
  /** Lookups tab: header → distinct values (same keys as sheet row 1, e.g. "Payment Status"). */
  lookupColumns: Record<string, string[]>;
}

export interface SubscriptionFilters {
  search: string;
  department: string;
  status: string;
  billingCycle: string;
  category: string;
}

// ─── Constants ──────────────────────────────────────────────────────────────

export const DEPARTMENTS = [
  'Engineering', 'Sales', 'Marketing', 'Finance', 'HR', 'Operations', 'All Depts',
] as const;

export const CATEGORIES = [
  'DevTools', 'Cloud', 'Collaboration', 'Security', 'Marketing', 'HR', 'Analytics', 'Other',
] as const;

export const BILLING_CYCLES = ['Monthly', 'Quarterly', 'Annual', 'Custom'] as const;

export const CURRENCIES = ['USD', 'INR', 'GBP', 'EUR'] as const;

export const STATUSES = ['Active', 'Paused', 'Cancelled'] as const;

export const APPROVAL_STATUSES = ['Approved', 'Pending-Approval'] as const;

export const APPROVED_BUDGETS = ['Monthly', 'Quarterly', 'Annually', 'One Time'] as const;

export const PAYMENT_STATUSES = ['Paid', 'Unpaid', 'Overdue', 'Cancelled', 'Draft'] as const;
